import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class PnsBundleProduct extends HTMLElement {
  #abortController;
  #isSubmitting = false;
  #hasInteracted = false;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });

    this.#initializeGroups();
    this.#updateState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get groups() {
    return Array.from(this.querySelectorAll('[data-bundle-group]'));
  }

  get quantityInput() {
    return this.querySelector('[data-bundle-quantity]');
  }

  get submitButtons() {
    return Array.from(this.querySelectorAll('[data-bundle-submit]'));
  }

  get discountCode() {
    return (this.dataset.bundleDiscountCode || '').trim();
  }

  #initializeGroups() {
    for (const group of this.groups) {
      const selectedColor = group.querySelector('[data-bundle-color][aria-pressed="true"]');
      if (!(selectedColor instanceof HTMLButtonElement)) continue;

      const selectedValue = group.querySelector('[data-bundle-selected-color]');
      if (selectedValue) selectedValue.textContent = selectedColor.dataset.colorLabel || '';

      const productId = selectedColor.dataset.productId;
      for (const panel of group.querySelectorAll('[data-bundle-size-panel]')) {
        panel.toggleAttribute('hidden', panel.getAttribute('data-product-id') !== productId);
      }
    }
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const colorButton = event.target.closest('[data-bundle-color]');
    if (colorButton instanceof HTMLButtonElement && !colorButton.disabled) {
      this.#selectColor(colorButton);
      return;
    }

    const sizeButton = event.target.closest('[data-bundle-size]');
    if (sizeButton instanceof HTMLButtonElement && !sizeButton.disabled) {
      this.#selectSize(sizeButton);
      return;
    }

    const quantityAction = event.target.closest('[data-bundle-quantity-action]');
    if (quantityAction instanceof HTMLButtonElement) {
      this.#changeQuantity(quantityAction.dataset.bundleQuantityAction === 'increase' ? 1 : -1);
      return;
    }

    const submitButton = event.target.closest('[data-bundle-submit]');
    if (submitButton instanceof HTMLButtonElement) {
      this.#submitBundle(submitButton.dataset.bundleSubmit === 'checkout');
    }
  };

  #handleChange = (event) => {
    if (event.target !== this.quantityInput) return;
    this.#normalizeQuantity();
    this.#updateState();
  };

  #selectColor(button) {
    const group = button.closest('[data-bundle-group]');
    if (!(group instanceof HTMLElement)) return;

    this.#hasInteracted = true;

    for (const color of group.querySelectorAll('[data-bundle-color]')) {
      const selected = color === button;
      color.setAttribute('aria-pressed', String(selected));
      color.classList.toggle('pns-product-colour-swatch--active', selected);
    }

    const productId = button.dataset.productId || '';
    for (const panel of group.querySelectorAll('[data-bundle-size-panel]')) {
      panel.toggleAttribute('hidden', panel.getAttribute('data-product-id') !== productId);
      for (const size of panel.querySelectorAll('[data-bundle-size]')) {
        size.setAttribute('aria-pressed', 'false');
      }
    }

    const selectedValue = group.querySelector('[data-bundle-selected-color]');
    if (selectedValue) selectedValue.textContent = button.dataset.colorLabel || '';

    delete group.dataset.variantId;
    delete group.dataset.variantPrice;
    delete group.dataset.variantMax;
    delete group.dataset.sizeLabel;

    const status = group.querySelector('[data-bundle-group-status]');
    if (status) status.textContent = '사이즈를 선택해주세요';

    this.#markIncompleteGroups();
    this.#clearMessage();
    this.#updateState();
  }

  #selectSize(button) {
    const group = button.closest('[data-bundle-group]');
    const panel = button.closest('[data-bundle-size-panel]');
    if (!(group instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    this.#hasInteracted = true;

    for (const size of panel.querySelectorAll('[data-bundle-size]')) {
      size.setAttribute('aria-pressed', String(size === button));
    }

    group.dataset.variantId = button.dataset.variantId || '';
    group.dataset.variantPrice = button.dataset.variantPrice || '0';
    group.dataset.variantMax = button.dataset.quantityMax || '';
    group.dataset.sizeLabel = button.dataset.sizeLabel || '';

    const colorLabel = group.querySelector('[data-bundle-selected-color]')?.textContent?.trim();
    const status = group.querySelector('[data-bundle-group-status]');
    if (status) {
      status.textContent = [colorLabel, button.dataset.sizeLabel].filter(Boolean).join(' · ');
    }

    this.#markIncompleteGroups();
    this.#clearMessage();
    this.#updateState();
  }

  #changeQuantity(delta) {
    const input = this.quantityInput;
    if (!(input instanceof HTMLInputElement)) return;

    const current = this.#getQuantity();
    input.value = String(current + delta);
    this.#normalizeQuantity();
    this.#updateState();
  }

  #normalizeQuantity() {
    const input = this.quantityInput;
    if (!(input instanceof HTMLInputElement)) return;

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const requested = Number.parseInt(input.value, 10);
    const normalized = Math.min(max, Math.max(min, Number.isFinite(requested) ? requested : min));

    input.value = String(normalized);
  }

  #getQuantity() {
    const input = this.quantityInput;
    if (!(input instanceof HTMLInputElement)) return 1;
    return Math.max(1, Number.parseInt(input.value, 10) || 1);
  }

  #isComplete() {
    return this.groups.length > 0 && this.groups.every((group) => Boolean(group.dataset.variantId));
  }

  #markIncompleteGroups() {
    for (const group of this.groups) {
      group.classList.toggle('pns-bundle-group--incomplete', !group.dataset.variantId);
    }
  }

  #syncQuantityConstraints() {
    const input = this.quantityInput;
    if (!(input instanceof HTMLInputElement)) return;

    const selectedVariantLimits = new Map();

    for (const group of this.groups) {
      const variantId = group.dataset.variantId;
      const maximum = Number.parseInt(group.dataset.variantMax || '', 10);
      if (!variantId || !Number.isFinite(maximum) || maximum <= 0) continue;

      const current = selectedVariantLimits.get(variantId) || { occurrences: 0, maximum };
      current.occurrences += 1;
      current.maximum = Math.min(current.maximum, maximum);
      selectedVariantLimits.set(variantId, current);
    }

    const selectedMaximums = Array.from(selectedVariantLimits.values()).map(({ occurrences, maximum }) =>
      Math.max(1, Math.floor(maximum / occurrences))
    );

    if (selectedMaximums.length) {
      input.max = String(Math.min(...selectedMaximums));
    } else {
      input.removeAttribute('max');
    }

    this.#normalizeQuantity();

    const quantity = this.#getQuantity();
    const decrease = this.querySelector('[data-bundle-quantity-action="decrease"]');
    const increase = this.querySelector('[data-bundle-quantity-action="increase"]');

    if (decrease instanceof HTMLButtonElement) decrease.disabled = quantity <= 1 || this.#isSubmitting;
    if (increase instanceof HTMLButtonElement) {
      increase.disabled = (input.max !== '' && quantity >= Number(input.max)) || this.#isSubmitting;
    }
    input.disabled = this.#isSubmitting;
  }

  #updateState() {
    const complete = this.#isComplete();
    const total = this.querySelector('[data-bundle-total]');

    this.#syncQuantityConstraints();

    if (total) {
      if (this.discountCode) {
        total.textContent = '장바구니에서 확인';
      } else if (complete) {
        const unitTotal = this.groups.reduce(
          (sum, group) => sum + (Number.parseInt(group.dataset.variantPrice || '0', 10) || 0),
          0
        );
        total.textContent = this.#formatMoney(unitTotal * this.#getQuantity());
      } else {
        total.textContent = '사이즈를 선택해주세요';
      }
    }

    for (const button of this.submitButtons) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = !complete || this.#isSubmitting;
      }
    }

    if (this.#hasInteracted) this.#markIncompleteGroups();
  }

  #formatMoney(value) {
    const moneyFormat = this.dataset.moneyFormat || '₩{{amount_no_decimals}}';
    const currency = this.dataset.currency || 'KRW';
    let currencyPrecision = 2;

    try {
      currencyPrecision = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits;
    } catch (error) {
      console.warn('Could not determine the currency precision.', error);
    }

    // Liquid appends two decimal places to raw prices for currencies without subunits (for example, KRW).
    const normalizedValue = currencyPrecision === 0 ? value / 100 : value;
    return formatMoney(normalizedValue, moneyFormat, currency);
  }

  #buildItems(bundleKey) {
    const bundleQuantity = this.#getQuantity();
    const itemsByVariant = new Map();

    for (const group of this.groups) {
      const variantId = Number.parseInt(group.dataset.variantId || '', 10);
      if (!Number.isFinite(variantId)) continue;

      const existing = itemsByVariant.get(variantId);
      if (existing) {
        existing.quantity += bundleQuantity;
      } else {
        itemsByVariant.set(variantId, {
          id: variantId,
          quantity: bundleQuantity,
          properties: {
            _bundle_name: this.dataset.bundleName || '',
            _bundle_key: bundleKey,
          },
        });
      }
    }

    return Array.from(itemsByVariant.values());
  }

  #getRootRoute() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  #createBundleKey() {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  #getCheckoutUrl(root, cart) {
    const checkoutUrl = `${root}checkout`;
    if (!this.discountCode) return checkoutUrl;

    const appliedCodes = Array.isArray(cart?.discount_codes)
      ? cart.discount_codes
          .filter((discount) => discount?.applicable === true && typeof discount.code === 'string')
          .map((discount) => discount.code.trim())
          .filter(Boolean)
      : [];
    const checkoutDiscounts = appliedCodes.length ? appliedCodes : [this.discountCode];

    return `${checkoutUrl}?discount=${encodeURIComponent(checkoutDiscounts.join(','))}`;
  }

  async #applyBundleDiscount(root, bundleKey, addResponse) {
    const discountCode = this.discountCode;
    if (!discountCode) {
      return { attempted: false, applied: true, cart: null };
    }

    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: discountCode }),
      });

      const cart = await response.json().catch(() => ({}));
      if (!response.ok || cart.status) {
        console.error('Failed to apply bundle discount:', this.#getResponseError(cart));
        return { attempted: true, applied: false, cart: null };
      }

      const applied = this.#isBundleDiscountApplied(cart, discountCode, bundleKey, addResponse);
      if (!applied) {
        console.warn('Bundle discount was not confirmed by the cart response.');
      }

      return { attempted: true, applied, cart };
    } catch (error) {
      console.error('Failed to apply bundle discount:', error);
      return { attempted: true, applied: false, cart: null };
    }
  }

  #isBundleDiscountApplied(cart, discountCode, bundleKey, addResponse) {
    const normalizedCode = this.#normalizeDiscountCode(discountCode);
    const codeState = Array.isArray(cart?.discount_codes)
      ? cart.discount_codes.find((discount) => {
          return this.#normalizeDiscountCode(discount?.code) === normalizedCode;
        })
      : null;

    if (!codeState || codeState.applicable !== true) return false;

    const bundleItems = Array.isArray(cart?.items)
      ? cart.items.filter((item) => item?.properties?._bundle_key === bundleKey)
      : [];

    if (!bundleItems.length) return false;

    const hasMatchingAllocation = bundleItems.some((item) => {
      const allocations = Array.isArray(item?.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      const discounts = Array.isArray(item?.discounts) ? item.discounts : [];

      return (
        allocations.some((allocation) => {
          const application = allocation?.discount_application;
          const applicationCode = application?.code || application?.title;
          const amount = Number(allocation?.amount ?? application?.total_allocated_amount ?? 0);
          return amount > 0 && this.#normalizeDiscountCode(applicationCode) === normalizedCode;
        }) ||
        discounts.some((discount) => {
          const applicationCode = discount?.code || discount?.title;
          return Number(discount?.amount ?? 0) > 0 && this.#normalizeDiscountCode(applicationCode) === normalizedCode;
        })
      );
    });

    if (hasMatchingAllocation) return true;

    return this.#getBundleDiscountTotal(cart, bundleKey) > this.#getBundleDiscountTotal(addResponse, bundleKey);
  }

  #getBundleDiscountTotal(cart, bundleKey) {
    if (!Array.isArray(cart?.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (item?.properties?._bundle_key !== bundleKey) return total;

      const explicitDiscount = Number(item.line_level_total_discount ?? item.total_discount);
      if (Number.isFinite(explicitDiscount)) return total + explicitDiscount;

      const originalLinePrice = Number(item.original_line_price);
      const finalLinePrice = Number(item.final_line_price);
      if (!Number.isFinite(originalLinePrice) || !Number.isFinite(finalLinePrice)) return total;

      return total + Math.max(0, originalLinePrice - finalLinePrice);
    }, 0);
  }

  #normalizeDiscountCode(value) {
    return typeof value === 'string' ? value.trim().toLocaleUpperCase() : '';
  }

  async #fetchCart(root) {
    try {
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (response.ok) return await response.json();
    } catch (error) {
      console.warn('Bundle was added, but the cart count could not be fetched.', error);
    }

    return null;
  }

  #dispatchCartAdd(cart, addResponse, totalAdded, includeAddSections) {
    const eventSource = cart ? 'pns-bundle-product' : 'product-form-component';
    const itemCount = cart?.item_count ?? totalAdded;

    this.dispatchEvent(
      new CartAddEvent(cart ?? undefined, this.id, {
        source: eventSource,
        itemCount,
        productId: this.dataset.productId,
        sections: includeAddSections ? addResponse.sections : undefined,
      })
    );
  }

  async #submitBundle(redirectToCheckout) {
    if (this.#isSubmitting) return;

    if (!this.#isComplete()) {
      this.#hasInteracted = true;
      this.#markIncompleteGroups();
      this.#showMessage('모든 그룹의 사이즈를 선택해주세요.', true);
      return;
    }

    const bundleKey = this.#createBundleKey();
    const items = this.#buildItems(bundleKey);
    if (!items.length) {
      this.#showMessage('선택된 상품을 확인해주세요.', true);
      return;
    }

    this.#setSubmitting(true, redirectToCheckout);

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.getAttribute('data-section-id'))
      .filter(Boolean);

    const payload = { items };
    if (sectionIds.length) {
      payload.sections = Array.from(new Set(sectionIds)).join(',');
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const root = this.#getRootRoute();
    let addResponse;

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      addResponse = await response.json().catch(() => ({}));
      if (!response.ok || addResponse.status) {
        throw new Error(this.#getResponseError(addResponse));
      }
    } catch (error) {
      console.error('Failed to add bundle to cart:', error);
      this.#showMessage(error instanceof Error ? error.message : '상품을 추가하지 못했습니다.', true);
      this.#setSubmitting(false, redirectToCheckout);
      return;
    }

    try {
      const totalAdded = items.reduce((sum, item) => sum + item.quantity, 0);
      const discountResult = await this.#applyBundleDiscount(root, bundleKey, addResponse);
      const shouldFetchCart = discountResult.attempted || !redirectToCheckout;
      const cart = discountResult.cart ?? (shouldFetchCart ? await this.#fetchCart(root) : null);

      this.#dispatchCartAdd(cart, addResponse, totalAdded, !discountResult.attempted);

      if (!discountResult.applied) {
        this.#showMessage(
          '상품은 장바구니에 추가되었지만 번들 할인이 적용되지 않았습니다. 할인 조건을 확인해주세요.',
          true
        );
        return;
      }

      this.#showMessage(
        discountResult.attempted
          ? '선택한 상품이 장바구니에 추가되었으며 번들 할인이 적용되었습니다.'
          : '선택한 상품이 장바구니에 추가되었습니다.',
        false
      );

      if (redirectToCheckout) window.location.assign(this.#getCheckoutUrl(root, cart));
    } finally {
      this.#setSubmitting(false, redirectToCheckout);
    }
  }

  #getResponseError(response) {
    if (typeof response?.description === 'string') return response.description;
    if (typeof response?.message === 'string') return response.message;

    if (response?.errors && typeof response.errors === 'object') {
      return Object.values(response.errors).flat().join(' ');
    }

    return '재고를 확인한 후 다시 시도해주세요.';
  }

  #setSubmitting(isSubmitting, redirectToCheckout = false) {
    this.#isSubmitting = isSubmitting;
    this.setAttribute('aria-busy', String(isSubmitting));
    this.classList.toggle('pns-bundle-product--loading', isSubmitting);

    for (const control of this.querySelectorAll('[data-bundle-color], [data-bundle-size]')) {
      if (!(control instanceof HTMLButtonElement)) continue;

      if (isSubmitting && !control.disabled) {
        control.dataset.bundleDisabledByLoading = 'true';
        control.disabled = true;
      } else if (!isSubmitting && control.dataset.bundleDisabledByLoading === 'true') {
        control.disabled = false;
        delete control.dataset.bundleDisabledByLoading;
      }
    }

    for (const button of this.submitButtons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const label = button.querySelector('[data-bundle-button-label]');
      if (label) {
        label.textContent =
          isSubmitting && button.dataset.bundleSubmit === (redirectToCheckout ? 'checkout' : 'cart')
            ? '처리 중...'
            : button.dataset.defaultLabel || '';
      }
    }

    this.#updateState();
  }

  #showMessage(message, isError) {
    const messageElement = this.querySelector('[data-bundle-message]');
    if (!messageElement) return;

    messageElement.textContent = message;
    messageElement.classList.toggle('pns-bundle-product__message--error', isError);
    messageElement.classList.toggle('pns-bundle-product__message--success', !isError);
  }

  #clearMessage() {
    const messageElement = this.querySelector('[data-bundle-message]');
    if (!messageElement) return;

    messageElement.textContent = '';
    messageElement.classList.remove(
      'pns-bundle-product__message--error',
      'pns-bundle-product__message--success'
    );
  }
}

if (!customElements.get('pns-bundle-product')) {
  customElements.define('pns-bundle-product', PnsBundleProduct);
}
